/* Dongar AI Assistant — storefront widget.
   Talks to /api/assistant/chat (dongar-inventory) over SSE and renders the
   conversation. Cards (fitment, products, videos, guides) are built from
   `tool_result` events only — never from model text — so the UI cannot show
   a link, price or fitment the backend did not return (prices are live store prices only).

   Events emitted on window (CustomEvent 'dgr:assistant', detail.type):
     open · message · fitment_shown · product_shown · product_click · add_to_cart · amazon_click · video_click · error
   Also pushed to window.dataLayer as { event: 'assistant_<type>', ... }.
*/
(function () {
  'use strict';

  var COOKIE_ENABLE = 'dongar_assistant';      // on | off (internal override)
  var COOKIE_BUCKET = 'dongar_ab_assistant';   // 0..99 sticky bucket
  var COUNTRY_KEY = 'dongar_country_override'; // same key the finder uses
  var SESSION_KEY = 'dongar_assistant_session';
  var MAX_HISTORY = 12;

  /* ── utils ─────────────────────────────────────────────────────────── */
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax; Secure';
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function safeUrl(u) {
    if (typeof u !== 'string') return null;
    if (/^\/(?!\/)/.test(u)) return u;
    return /^https:\/\//i.test(u) ? u : null;
  }
  // Fit Finder A/B/C test: a link the assistant gives to the Fit Finder (/pages/fit-finder, the test's entry
  // point) is marked src=assistant so the split can send assistant-arm visitors to the real finder instead of
  // bouncing them back here, and the finder funnel can tell these visits apart (events carry source=assistant).
  function markFinderLink(u) {
    if (!u) return u;
    try {
      var url = new URL(u, location.origin);
      if (!/(^|\.)dongar\.tech$/i.test(url.hostname) || !/^\/pages\/fit-finder\/?$/i.test(url.pathname)) return u;
      if (!url.searchParams.has('src')) url.searchParams.set('src', 'assistant');
      return url.toString();
    } catch (e) { return u; }
  }
  function money(n) {
    if (n == null || isNaN(n)) return '';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n); } catch (e) { return '$' + Number(n).toFixed(2); }
  }
  var BEACON_TYPES = { add_to_cart: 1, amazon_click: 1, video_click: 1 }; // 'open' is high-volume and unused by the dashboard — add later if it becomes a denominator
  var beaconUrl = null; // set by the widget from its chat endpoint: /api/assistant/chat → /api/assistant/event
  function emit(type, detail) {
    var d = Object.assign({ type: type }, detail || {});
    try { window.dispatchEvent(new CustomEvent('dgr:assistant', { detail: d })); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'assistant_' + type }, detail || {})); } catch (e) {}
    // Conversion beacon (assistant_events on the inventory app) keyed by the session id so it joins the
    // conversation log and tagged orders. Fire-and-forget; must never affect the UI.
    if (BEACON_TYPES[type] && beaconUrl && d.session_id) {
      try {
        fetch(beaconUrl, {
          method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: type, session_id: d.session_id, sku: d.sku || null, variant_id: d.variant_id || null,
            marketplace: d.marketplace || null, country: d.country || null, page_url: location.href.slice(0, 500) })
        }).catch(function () {});
      } catch (e) {}
    }
  }

  /* Minimal, escaped markdown: paragraphs, **bold**, `code`, [text](https://…), "- " lists. */
  // The model ends each reply with `>>> ["follow-up", …]` (prompt rule 11). Hide it from the bubble
  // while streaming; the server sends the parsed items as a 'suggestions' event (fallback: parse here).
  function stripFollowups(text) {
    var at = text.lastIndexOf('>>> [') >= 0 ? text.lastIndexOf('>>> [') : text.lastIndexOf('>>>[');
    return at >= 0 ? text.slice(0, at).replace(/\s+$/, '') : text;
  }
  function parseFollowups(text) {
    var at = text.lastIndexOf('>>> [') >= 0 ? text.lastIndexOf('>>> [') : text.lastIndexOf('>>>[');
    if (at < 0) return [];
    try { var arr = JSON.parse(text.slice(at).replace(/^>>>\s*/, '')); return Array.isArray(arr) ? arr.filter(function (x) { return typeof x === 'string' && x.trim(); }).slice(0, 3) : []; } catch (e) { return []; }
  }

  function renderMarkdown(text) {
    var blocks = String(text || '').trim().split(/\n{2,}/);
    return blocks.map(function (block) {
      var lines = block.split('\n');
      var isList = lines.length > 0 && lines.every(function (l) { return /^\s*[-*•]\s+/.test(l); });
      if (isList) {
        return '<ul>' + lines.map(function (l) { return '<li>' + inline(l.replace(/^\s*[-*•]\s+/, '')) + '</li>'; }).join('') + '</ul>';
      }
      return '<p>' + inline(block).replace(/\n/g, '<br>') + '</p>';
    }).join('');
  }
  // Short human label for a URL the model pasted bare: "dongar.tech/pages/fit-finder", no scheme/query, trimmed.
  function shortLabel(url) {
    var l = url.replace(/^https?:\/\/(www\.)?/, '').replace(/[?#].*$/, '').replace(/\/$/, '');
    return l.length > 48 ? l.slice(0, 45) + '…' : l;
  }
  function inline(s) {
    var out = esc(s);
    out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Markdown links first, parked as placeholders so the bare-URL pass below can't touch their hrefs.
    var links = [];
    out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, function (_, label, url) {
      var u = markFinderLink(safeUrl(url.replace(/&amp;/g, '&'))); // esc() above already encoded the text; don't double-encode the href
      // A label that is itself a URL gets shortened too.
      var text = /^https?:\/\//.test(label) ? shortLabel(label) : label;
      links.push(u ? '<a href="' + esc(u) + '" target="_blank" rel="noopener">' + text + '</a>' : text);
      return '\u0000' + (links.length - 1) + '\u0000';
    });
    // Bare URLs (Kevin, Sep 30 2026: never show a long URL — make it clickable with a short label).
    out = out.replace(/(^|[\s(])(https?:\/\/[^\s<)]+?)([.,;:!?)]*)(?=\s|$)/g, function (_, pre, url, trail) {
      var u = markFinderLink(safeUrl(url.replace(/&amp;/g, '&')));
      return pre + (u ? '<a href="' + esc(u) + '" target="_blank" rel="noopener">' + esc(shortLabel(url)) + '</a>' : url) + trail;
    });
    out = out.replace(/\u0000(\d+)\u0000/g, function (_, i) { return links[Number(i)]; });
    return out;
  }

  var TOOL_LABELS = {
    find_compatible_adapters: 'Checking fitment…',
    get_product_details: 'Checking stock…',
    lookup_dashcam_device: 'Looking up your camera…',
    find_videos: 'Finding install videos…',
    get_install_guidance: 'Pulling install guidance…'
  };

  /* ── widget ───────────────────────────────────────────────────────── */
  function Assistant(root) {
    this.root = root;
    this.api = root.getAttribute('data-api');
    try { beaconUrl = this.api ? this.api.replace(/\/api\/assistant\/chat\/?(\?.*)?$/, '/api/assistant/event') : null; if (beaconUrl === this.api) beaconUrl = null; } catch (e) { beaconUrl = null; }
    this.mode = root.getAttribute('data-mode') || 'floating';
    this.country = this.resolveCountry(root.getAttribute('data-country') || 'US');
    this.cartRedirect = root.getAttribute('data-cart-redirect') === 'true';
    this.history = [];
    this.busy = false;
    this.opened = false;
    this.sessionId = this.session();

    this.panel = root.querySelector('.dgr-ai__panel');
    this.log = root.querySelector('[data-dgr-ai-log]');
    this.suggest = root.querySelector('[data-dgr-ai-suggest]');
    this.form = root.querySelector('[data-dgr-ai-form]');
    this.input = root.querySelector('[data-dgr-ai-input]');
    this.send = root.querySelector('[data-dgr-ai-send]');

    var self = this;
    root.querySelectorAll('[data-dgr-ai-toggle]').forEach(function (b) { b.addEventListener('click', function () { self.toggle(); }); });
    this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(); });
    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); self.submit(); }
    });
    this.input.addEventListener('input', function () { self.autosize(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.mode === 'floating' && self.opened) self.toggle(false); });

    var welcome = root.getAttribute('data-welcome');
    if (welcome) this.addBot(welcome, true);
    this.renderSuggestions((root.getAttribute('data-suggestions') || '').split('\n').map(function (s) { return s.trim(); }).filter(Boolean));

    root.hidden = false;
    if (this.mode === 'inline') { this.opened = true; emit('open', { mode: 'inline', session_id: this.sessionId }); }
    else if (root.getAttribute('data-open-on-load') === 'true') this.toggle(true);
  }

  Assistant.prototype.resolveCountry = function (fallback) {
    try {
      var o = localStorage.getItem(COUNTRY_KEY);
      if (o && /^[A-Za-z]{2}$/.test(o)) return o.toUpperCase();
    } catch (e) {}
    if (window.DONGAR_COUNTRY && /^[A-Za-z]{2}$/.test(window.DONGAR_COUNTRY)) return String(window.DONGAR_COUNTRY).toUpperCase();
    return /^[A-Za-z]{2}$/.test(fallback) ? fallback.toUpperCase() : 'US';
  };
  Assistant.prototype.session = function () {
    try {
      var s = sessionStorage.getItem(SESSION_KEY);
      if (!s) { s = uuid().replace(/-/g, ''); sessionStorage.setItem(SESSION_KEY, s); }
      return s;
    } catch (e) { return uuid().replace(/-/g, ''); }
  };
  Assistant.prototype.toggle = function (force) {
    var open = typeof force === 'boolean' ? force : !this.opened;
    this.opened = open;
    this.root.classList.toggle('is-open', open);
    if (this.mode === 'floating') this.panel.hidden = !open;
    var launcher = this.root.querySelector('.dgr-ai__launcher');
    if (launcher) launcher.setAttribute('aria-expanded', String(open));
    if (open) { emit('open', { mode: this.mode, session_id: this.sessionId }); this.input.focus(); this.scroll(); }
  };
  Assistant.prototype.autosize = function () {
    this.input.style.height = 'auto';
    this.input.style.height = Math.min(120, this.input.scrollHeight) + 'px';
  };
  Assistant.prototype.scroll = function () { this.log.scrollTop = this.log.scrollHeight; };

  Assistant.prototype.renderSuggestions = function (items, followup) {
    var self = this;
    this.suggest.innerHTML = '';
    items.slice(0, 6).forEach(function (q) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'dgr-ai__chip' + (followup ? ' dgr-ai__chip--followup' : ''); b.textContent = q;
      b.addEventListener('click', function () { self.input.value = q; self.submit(); });
      self.suggest.appendChild(b);
    });
  };

  Assistant.prototype.addUser = function (text) {
    var el = document.createElement('div');
    el.className = 'dgr-ai__msg dgr-ai__msg--user'; el.textContent = text;
    this.log.appendChild(el); this.scroll();
  };
  Assistant.prototype.addBot = function (text, isWelcome) {
    var el = document.createElement('div');
    el.className = 'dgr-ai__msg dgr-ai__msg--bot'; el.innerHTML = renderMarkdown(text);
    this.log.appendChild(el); this.scroll();
    if (!isWelcome) this.history.push({ role: 'assistant', content: text });
    return el;
  };
  Assistant.prototype.addError = function (text) {
    var el = document.createElement('div');
    el.className = 'dgr-ai__msg dgr-ai__msg--error'; el.textContent = text;
    this.log.appendChild(el); this.scroll();
    emit('error', { message: text });
  };
  Assistant.prototype.status = function (label) {
    this.clearStatus();
    var el = document.createElement('div');
    el.className = 'dgr-ai__status'; el.setAttribute('data-dgr-ai-status', ''); el.textContent = label;
    this.log.appendChild(el); this.scroll();
  };
  Assistant.prototype.clearStatus = function () {
    this.log.querySelectorAll('[data-dgr-ai-status]').forEach(function (n) { n.remove(); });
  };

  /* ── send / stream ────────────────────────────────────────────────── */
  Assistant.prototype.submit = function () {
    var text = (this.input.value || '').trim();
    if (!text || this.busy) return;
    this.input.value = ''; this.autosize();
    this.suggest.innerHTML = '';
    this.addUser(text);
    this.history.push({ role: 'user', content: text });
    emit('message', { turn: this.history.filter(function (m) { return m.role === 'user'; }).length, country: this.country });
    this.stream();
  };

  Assistant.prototype.setBusy = function (b) {
    this.busy = b; this.send.disabled = b; this.input.disabled = b;
    if (!b) this.input.focus();
  };

  Assistant.prototype.stream = async function () {
    var self = this;
    this.setBusy(true);
    // Reply text arrives in one burst at the end of the turn (observed Oct 18 2026: all deltas within ~10 ms),
    // so show activity from the first millisecond instead of a greyed-out composer.
    this.status('Thinking…');
    var ctrl = new AbortController();
    this.abort = ctrl;
    var bubble = null, text = '', gotDone = false, hadError = false, followups = [];

    var payload = {
      messages: this.history.slice(-MAX_HISTORY).filter(function (m, i, arr) {
        // API requires first and last to be user turns.
        return !(i === 0 && m.role === 'assistant');
      }),
      country: this.country,
      page_url: location.href.split('#')[0].slice(0, 500),
      session_id: this.sessionId
    };

    try {
      var res = await fetch(this.api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: ctrl.signal
      });
      if (res.status === 429) throw new Error('rate');
      if (res.status === 503) throw new Error(res.headers.get('x-dongar-assistant') === 'budget' ? 'budget' : 'disabled');
      if (!res.ok || !res.body) throw new Error('HTTP ' + res.status);

      var reader = res.body.getReader();
      var dec = new TextDecoder();
      var buf = '';
      while (true) {
        var chunk = await reader.read();
        if (chunk.done) break;
        buf += dec.decode(chunk.value, { stream: true });
        var parts = buf.split('\n\n');
        buf = parts.pop();
        for (var i = 0; i < parts.length; i++) {
          var line = parts[i].split('\n').filter(function (l) { return l.indexOf('data:') === 0; }).map(function (l) { return l.slice(5).trim(); }).join('');
          if (!line) continue;
          var ev;
          try { ev = JSON.parse(line); } catch (e) { continue; }
          if (ev.type === 'text') {
            this.clearStatus();
            if (!bubble) { bubble = document.createElement('div'); bubble.className = 'dgr-ai__msg dgr-ai__msg--bot dgr-ai__cursor'; this.log.appendChild(bubble); }
            text += ev.text;
            bubble.innerHTML = renderMarkdown(stripFollowups(text));
            this.scroll();
          } else if (ev.type === 'suggestions') {
            if (Array.isArray(ev.items) && ev.items.length) { followups = ev.items; }
          } else if (ev.type === 'tool_start') {
            if (bubble) { bubble.classList.remove('dgr-ai__cursor'); bubble = null; if (text) { this.history.push({ role: 'assistant', content: stripFollowups(text) }); text = ''; } }
            this.status(TOOL_LABELS[ev.name] || 'Working…');
          } else if (ev.type === 'tool_result') {
            this.clearStatus();
            this.renderToolResult(ev);
            this.status('Thinking…'); // model is composing (or calling another tool) after this result
          } else if (ev.type === 'error') {
            hadError = true;
            this.clearStatus();
            this.addError(ev.message || 'Something went wrong.');
          } else if (ev.type === 'done') {
            gotDone = true;
            this.clearStatus();
            if (!followups.length) followups = parseFollowups(text); // older backend: marker still in the text
            if (bubble) { bubble.classList.remove('dgr-ai__cursor'); bubble.innerHTML = renderMarkdown(stripFollowups(text)); }
            if (text) this.history.push({ role: 'assistant', content: stripFollowups(text) });
            else if (!bubble && ev.text) this.addBot(ev.text);
            // Claude-style follow-ups: chips under the reply, composer stays open for anything else.
            if (followups.length) { this.renderSuggestions(followups, true); emit('followups', { count: followups.length }); }
          }
        }
      }
      if (!gotDone && text) this.history.push({ role: 'assistant', content: stripFollowups(text) });
      // A backend error with no answer: drop the user turn so history stays user/assistant alternating.
      if (hadError && !text && this.history.length && this.history[this.history.length - 1].role === 'user') this.history.pop();
    } catch (err) {
      this.clearStatus();
      if (bubble) bubble.classList.remove('dgr-ai__cursor');
      if (err && err.name === 'AbortError') return;
      var msg = err && err.message === 'rate' ? 'You’ve sent a lot of messages in a short time. Please wait a minute and try again.'
        : err && err.message === 'budget' ? 'The assistant has reached its daily limit while we test it. The Fit Finder (dongar.tech/pages/fit-finder) has every confirmed fit, or email support@dongar.tech.'
        : err && err.message === 'disabled' ? 'The assistant is offline right now. Use the finder above, or email support@dongar.tech.'
        : 'I couldn’t reach the assistant. Please try again, or use the finder above.';
      this.addError(msg);
      // Keep history consistent: drop the user turn that failed so the next send is valid.
      if (this.history.length && this.history[this.history.length - 1].role === 'user' && !text) this.history.pop();
    } finally {
      this.setBusy(false);
      this.scroll();
    }
  };

  /* ── cards from tool results ──────────────────────────────────────── */
  Assistant.prototype.renderToolResult = function (ev) {
    var out = ev.output;
    if (!out || out.error) return;
    // Funnel signals (fitment result / product cards shown) for the A/B test tracking; never affects rendering.
    try {
      if (ev.name === 'find_compatible_adapters' && out.fitments && out.fitments.length) {
        var rv = out.resolved || {};
        out.fitments.slice(0, 2).forEach(function (f) {
          emit('fitment_shown', {
            make: f.make || rv.make || null, model: f.model || rv.model || null,
            year: f.year_asked ? Number(f.year_asked) : (rv.year ? Number(rv.year) : null),
            skus: (f.options || []).map(function (o) { return o.sku; }).filter(Boolean),
            session_id: this.sessionId
          });
        }, this);
      } else if (ev.name === 'get_product_details' && out.items && out.items.length) {
        var shownSkus = out.items.filter(function (i) { return i && !i.coming_soon; }).slice(0, 4).map(function (i) { return i.sku; });
        if (shownSkus.length) emit('product_shown', Object.assign({ skus: shownSkus, session_id: this.sessionId }, this.vehicleDetail()));
      }
    } catch (e) {}
    switch (ev.name) {
      case 'find_compatible_adapters': return this.renderFit(out);
      case 'get_product_details': return this.renderProducts(out);
      case 'find_videos': return this.renderVideos(out);
      case 'get_install_guidance': return this.renderGuidance(out);
      default: return;
    }
  };

  // "includes 5A and 10B harnesses for two installation options" — never "includes DCA-5A"
  // (DCA-xx is a whole product SKU; a kit holds harnesses). Server sends kit_note; fall back for older builds.
  function kitNote(o) {
    if (o.kit_note) return o.kit_note;
    if (!o.kit_contains || !o.kit_contains.length) return '';
    return 'includes ' + o.kit_contains.map(function (s) { return String(s).replace(/^DCA-/, ''); }).join(' and ') + ' harnesses';
  }

  // Product page deep link: pin the variant and pre-fill the page's finder with the vehicle we just matched.
  Assistant.prototype.productLink = function (it) {
    var url = safeUrl(it.product_url);
    if (!url) return null;
    var buy = it.buy || {};
    try {
      var u = new URL(url, window.location.origin);
      if (buy.d2c && buy.d2c.variant_id && !u.searchParams.get('variant')) u.searchParams.set('variant', buy.d2c.variant_id);
      var v = this.lastVehicle || {};
      if (v.make) u.searchParams.set('make', v.make);
      if (v.model) u.searchParams.set('model', v.model);
      if (v.year) u.searchParams.set('year', v.year);
      return u.toString();
    } catch (e) { return url; }
  };

  Assistant.prototype.renderFit = function (out) {
    if (!out.fitments || !out.fitments.length) {
      if (out.zero_result && out.resolved && out.resolved.model) {
        var yrs = (out.covered_years_for_model || []);
        var el = document.createElement('div');
        el.className = 'dgr-ai__fit';
        el.innerHTML = '<h4>No confirmed fit yet</h4><div class="dgr-ai__fit-note">' +
          esc([out.resolved.year, out.resolved.make, out.resolved.model].filter(Boolean).join(' ')) +
          (yrs.length ? ' — covered years: <strong>' + esc(yrs[0] + (yrs.length > 1 ? '–' + yrs[yrs.length - 1] : '')) + '</strong>' : '') + '</div>';
        this.log.appendChild(el); this.scroll();
      }
      return;
    }
    var self = this;
    out.fitments.slice(0, 2).forEach(function (f) {
      var el = document.createElement('div');
      el.className = 'dgr-ai__fit';
      var title = [f.year_asked, f.make, f.model].filter(Boolean).join(' ');
      // Remembered so product "Details" links can pre-fill the product page's finder.
      self.lastVehicle = { make: f.make || '', model: f.model || '', year: f.year_asked ? String(f.year_asked) : '' };
      var h = '<h4>' + esc(title) + '</h4>';
      if (!f.year_asked) {
        // Collapse "2020–2025, 2022–2025, 2019, 2020–2021" into the covered span.
        var yrs = String((f.year_ranges || []).join(',')).match(/\d{4}/g) || [];
        if (yrs.length) {
          var lo = Math.min.apply(null, yrs), hi = Math.max.apply(null, yrs);
          h += '<div class="dgr-ai__fit-years">Covered years: ' + esc(lo === hi ? String(lo) : lo + '–' + hi) + ' · varies by trim</div>';
        }
      }
      (f.options || []).slice(0, 6).forEach(function (o) {
        var tier = o.tier === 'classic' ? 'Classic' : o.tier === 'pma' ? 'PRO · parking mode' : o.tier === 'pros' ? 'PRO+ · parking + smart' : o.tier;
        h += '<div class="dgr-ai__fit-opt"><span class="dgr-ai__sku">' + esc(o.sku) + '</span>' +
          '<span class="dgr-ai__badge' + (o.tier === 'classic' ? '' : ' dgr-ai__badge--tier') + '">' + esc(tier) + '</span>' +
          (o.install_location ? '<span class="dgr-ai__fit-note">' + esc(o.install_location.label) + '</span>' : '') +
          (kitNote(o) ? '<span class="dgr-ai__fit-note">' + esc(kitNote(o)) + '</span>' : '') +
          (!f.year_asked && o.years ? '<span class="dgr-ai__fit-note dgr-ai__fit-note--years">' + esc(o.years) + '</span>' : '') +
          '</div>';
      });
      if (f.required_cable) h += '<div class="dgr-ai__fit-note" style="margin-top:6px">+ ' + esc(f.required_cable.sku) + ' cable required for your camera</div>';
      el.innerHTML = h;
      self.log.appendChild(el);
    });
    this.scroll();
  };

  Assistant.prototype.renderProducts = function (out) {
    var items = (out.items || []).filter(function (i) { return i && !i.coming_soon; }).slice(0, 4);
    if (!items.length) return;
    var self = this;
    var wrap = document.createElement('div');
    wrap.className = 'dgr-ai__cards';
    items.forEach(function (it) {
      var card = document.createElement('div');
      card.className = 'dgr-ai__card' + (it.image_url ? '' : ' dgr-ai__card--nothumb');
      var img = safeUrl(it.image_url);
      var stock = it.in_stock === false ? '<span class="dgr-ai__badge dgr-ai__badge--warn">Out of stock</span>' : it.in_stock === true ? '<span class="dgr-ai__badge dgr-ai__badge--ok">In stock</span>' : '';
      var tier = it.tier === 'classic' ? 'Classic' : it.tier === 'pma' ? 'PRO' : it.tier === 'pros' ? 'PRO+' : it.tier === 'cable' ? 'Cable' : '';
      var h = (img ? '<img class="dgr-ai__thumb" src="' + esc(img) + '" alt="" loading="lazy">' : '') +
        '<div class="dgr-ai__card-body">' +
        '<p class="dgr-ai__card-title">' + esc(it.title || it.sku) + '</p>' +
        '<p class="dgr-ai__card-sub"><span class="dgr-ai__sku">' + esc(it.sku) + '</span>' + (it.install_location ? ' · ' + esc(it.install_location.label) : '') + (kitNote(it) ? ' · ' + esc(kitNote(it)) : '') + '</p>' +
        // Price only when the backend sent one: it sends price_usd solely from the live dongar.tech store price (US store),
        // never the catalog table (Kevin, Oct 2 2026: catalog prices aren't synced; live prices OK'd Oct 5 2026).
        // Converter cables: show the $2-off-with-an-adapter price like the finder does (backend sends price_with_adapter_usd).
        '<div class="dgr-ai__card-meta">' + (it.price_usd != null
          ? (it.price_with_adapter_usd != null && it.price_with_adapter_usd < it.price_usd
            ? '<span class="dgr-ai__price"><s class="dgr-ai__price-was">' + esc(money(it.price_usd)) + '</s> ' + esc(money(it.price_with_adapter_usd)) + '</span><span class="dgr-ai__price-note">with an adapter</span>'
            : '<span class="dgr-ai__price">' + esc(money(it.price_usd)) + '</span>')
          : '') +
        (tier ? '<span class="dgr-ai__badge">' + esc(tier) + '</span>' : '') + stock + '</div>' +
        '<div class="dgr-ai__card-actions" data-actions></div></div>';
      card.innerHTML = h;
      var actions = card.querySelector('[data-actions]');
      var buy = it.buy || {};
      if (buy.d2c && buy.d2c.variant_id && it.in_stock !== false) {
        var add = document.createElement('button');
        add.type = 'button'; add.className = 'dgr-ai__btn dgr-ai__btn--primary'; add.textContent = 'Add to cart';
        add.addEventListener('click', function () { self.addToCart(buy.d2c.variant_id, it.sku, add); });
        actions.appendChild(add);
      }
      if (buy.amazon && safeUrl(buy.amazon.url)) {
        var az = document.createElement('a');
        az.className = 'dgr-ai__btn dgr-ai__btn--amazon'; az.href = buy.amazon.url; az.target = '_blank'; az.rel = 'noopener nofollow sponsored';
        az.textContent = buy.marketplace === 'US' ? 'Buy on Amazon' : 'Buy on ' + (buy.amazon.domain || 'Amazon');
        az.addEventListener('click', function () { emit('amazon_click', Object.assign({ sku: it.sku, marketplace: buy.marketplace, session_id: self.sessionId }, self.vehicleDetail())); });
        actions.appendChild(az);
      }
      var link = self.productLink(it);
      if (link) {
        var view = document.createElement('a');
        view.className = 'dgr-ai__btn'; view.href = link; view.target = '_blank'; view.rel = 'noopener'; view.textContent = 'Details';
        view.addEventListener('click', function () { emit('product_click', Object.assign({ sku: it.sku, action: 'details', session_id: self.sessionId }, self.vehicleDetail())); });
        actions.appendChild(view);
      }
      wrap.appendChild(card);
    });
    this.log.appendChild(wrap); this.scroll();
  };

  Assistant.prototype.renderVideos = function (out) {
    var self = this;
    var vids = (out.videos || []).slice(0, 3);
    if (!vids.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'dgr-ai__cards';
    vids.forEach(function (v) {
      var url = safeUrl(v.url); if (!url) return;
      var a = document.createElement('a');
      a.className = 'dgr-ai__card'; a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.style.textDecoration = 'none'; a.style.color = 'inherit';
      var mins = v.duration_sec ? Math.round(v.duration_sec / 60) + ' min' : '';
      a.innerHTML = (safeUrl(v.thumbnail_url) ? '<img class="dgr-ai__thumb" src="' + esc(v.thumbnail_url) + '" alt="" loading="lazy">' : '<div class="dgr-ai__thumb"></div>') +
        '<div class="dgr-ai__card-body"><p class="dgr-ai__card-title">' + esc(v.title) + '</p>' +
        '<p class="dgr-ai__card-sub">' + esc([v.channel, v.vehicle, mins].filter(Boolean).join(' · ')) + '</p>' +
        '<div class="dgr-ai__card-meta"><span class="dgr-ai__badge">' + esc(v.content_type || 'video') + '</span><span class="dgr-ai__badge">YouTube ↗</span></div></div>';
      a.addEventListener('click', function () { emit('video_click', { url: url, session_id: self.sessionId }); });
      wrap.appendChild(a);
    });
    this.log.appendChild(wrap); this.scroll();
  };

  Assistant.prototype.renderGuidance = function (out) {
    var links = [];
    (out.written_guides || []).slice(0, 3).forEach(function (g) { if (safeUrl(g.url)) links.push({ label: g.title, url: g.url }); });
    (out.vehicle_guide_pages || []).slice(0, 3).forEach(function (g) { if (safeUrl(g.url)) links.push({ label: g.vehicle + ' guide', url: g.url }); });
    var photos = (out.connector_reference && out.connector_reference.photos) || [];
    if (!links.length && !photos.length) return;
    var el = document.createElement('div');
    el.className = 'dgr-ai__links';
    var h = links.map(function (l) { return '<a href="' + esc(l.url) + '" target="_blank" rel="noopener">' + esc(l.label) + ' ↗</a>'; }).join('');
    if (photos.length) h += '<div class="dgr-ai__photos">' + photos.slice(0, 4).map(function (p) { return safeUrl(p.url) ? '<img src="' + esc(p.url) + '" alt="' + esc(p.caption || '') + '" title="' + esc(p.caption || '') + '" loading="lazy">' : ''; }).join('') + '</div>';
    el.innerHTML = h;
    this.log.appendChild(el); this.scroll();
  };

  /* Vehicle the conversation is about (last fitment result), for funnel events. */
  Assistant.prototype.vehicleDetail = function () {
    var v = this.lastVehicle || {};
    return { make: v.make || null, model: v.model || null, year: v.year ? Number(v.year) : null };
  };

  /* ── cart ─────────────────────────────────────────────────────────── */
  Assistant.prototype.addToCart = async function (variantId, sku, btn) {
    var self = this;
    var orig = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      var sections = ['cart-drawer', 'cart-icon-bubble'];
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { _dongar_assistant: this.sessionId } }],
          sections: sections.join(','),
          sections_url: location.pathname
        })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var data = await res.json();
      emit('add_to_cart', Object.assign({ sku: sku, variant_id: String(variantId), country: this.country, session_id: this.sessionId }, this.vehicleDetail()));
      btn.textContent = 'Added ✓';
      if (this.cartRedirect) { location.href = '/cart'; return; }
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
        try { drawer.renderContents(data); } catch (e) {}
        if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
      } else {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) bubble.innerHTML = data.sections['cart-icon-bubble'];
      }
      setTimeout(function () { btn.disabled = false; btn.textContent = orig; }, 2500);
    } catch (err) {
      btn.disabled = false; btn.textContent = orig;
      self.addError('Could not add to cart. Try the product page instead.');
    }
  };

  /* ── gating + boot ────────────────────────────────────────────────── */
  function allowed(root) {
    var qs = new URLSearchParams(location.search).get('dongar_assistant');
    if (qs === 'on' || qs === 'off') setCookie(COOKIE_ENABLE, qs, 30);
    var override = cookie(COOKIE_ENABLE);
    if (override === 'on') return true;
    if (override === 'off') return false;
    var pct = parseInt(root.getAttribute('data-rollout') || '0', 10);
    if (!(pct > 0)) return false;
    if (pct >= 100) return true;
    var b = parseInt(cookie(COOKIE_BUCKET) || '', 10);
    if (isNaN(b)) { b = Math.floor(Math.random() * 100); setCookie(COOKIE_BUCKET, String(b), 90); }
    return b < pct;
  }

  function boot() {
    document.querySelectorAll('.dgr-ai[data-api]').forEach(function (root) {
      if (root.__dgrAi) return;
      if (!allowed(root)) { root.remove(); return; }
      root.__dgrAi = new Assistant(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.DongarAssistant = { boot: boot };
})();
